import React, { useState, useEffect } from 'react';
import {
    Box,
    IconButton,
    Typography,
    useTheme,
    Paper,
    Button,
    Stack,
    Container,
    alpha,
    TextField,
    InputAdornment,
    Tooltip,
    Snackbar,
    Alert
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AddIcon from '@mui/icons-material/Add';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { addFunds } from '../../services/backendAPI';
import AddBankModal from './AddBankModal';
import { fetchProfileDataAction } from '../../store/actions/profile';


const PayIn = ({ onClose, isModal }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const theme = useTheme();

    // Get user data from Redux store
    const userData = useSelector(state => state.profile.userData);
    const bankAccounts = userData?.bank_accounts || [];

    // Amount state and validation
    const [amount, setAmount] = useState('500');
    const [amountError, setAmountError] = useState('');

    // Modify snackbar state to be simpler
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'error'
    });

    const [showAddBankModal, setShowAddBankModal] = useState(false);
    const [selectedBank, setSelectedBank] = useState({
        amount: '',
        bank_account_number: '',
        bank_name: '',
        ifsc: '',
    });

    const [showAllBanks, setShowAllBanks] = useState(false);

    // Set default selected bank when component mounts or bank accounts change
    useEffect(() => {
        if (bankAccounts.length > 0) {
            setSelectedBank(bankAccounts[0].bank_account_number);
        }
    }, [bankAccounts]);

    const handleAmountChange = (event) => {
        const value = event.target.value;
        // Remove any non-numeric characters except decimal point
        const cleanValue = value.replace(/[^\d.]/g, '');
        // Ensure only one decimal point
        const parts = cleanValue.split('.');
        const formattedValue = parts[0] + (parts.length > 1 ? '.' + parts[1].slice(0, 2) : '');

        // Validate amount
        if (parseFloat(formattedValue) < 100) {
            setAmountError('Minimum amount is ₹500');
        // } else if (parseFloat(formattedValue) > 1000000) {
        //     setAmountError('Maximum amount is ₹10,00,000');
        } else {
            setAmountError('');
        }

        setAmount(formattedValue);
    };

    const handleClose = () => {
        if (onClose) {
            onClose();
        } else {
            dispatch({
                type: 'SET_NAVIGATED_TO_PAYIN',
                payload: false
            });
            navigate(-1);
        }
    };

    const handleAddBankClick = () => {
        setShowAddBankModal(true);
    };

    const handleAddBankClose = () => {
        setShowAddBankModal(false);
        dispatch(fetchProfileDataAction());
    };

    const handleAddFunds = async () => {
        let newWindow;
        try {
            // Safari popup workaround
            const isSafari = typeof window !== 'undefined' &&
                /^((?!chrome|android).)*safari/i.test(window.navigator.userAgent);
            if (isSafari) {
                newWindow = window.open('', '_blank');
            }
            let payload = {
                amount: String(Number(amount) * 100),
                client_id: localStorage.getItem("login_id"),
            };

            // Only add bank details if a bank is selected
            const selectedBankAccount = bankAccounts.find(bank => bank.bank_account_number === selectedBank);
            if (selectedBankAccount) {
                payload = {
                    ...payload,
                    bank_account_number: selectedBankAccount.bank_account_number,
                    bank_name: selectedBankAccount.bank_name,
                    ifsc: selectedBankAccount.ifsc,
                };
            }

            const { data } = await addFunds(payload);

            if (data?.data?.payUrl) {
                if (newWindow) {
                    newWindow.location = data.data.payUrl;
                } else {
                    window.open(data.data.payUrl, "_blank");
                }
            } else {
                if (newWindow) newWindow.close();
                throw new Error('Payment URL not received');
            }
        } catch (error) {
            if (newWindow) newWindow.close();
            setSnackbar({
                open: true,
                message: error?.message || error || 'Failed to initiate payment',
                severity: 'error'
            });
        }
    };

    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') return;
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    const isValidAmount = amount && !amountError && parseFloat(amount) >= 100;

    return (
        <Box sx={
            isModal
                ? {
                    width: '100%',
                    bgcolor: theme.palette.background.default,
                    px: 0,
                    borderRadius: 3,
                    boxShadow: 0,
                    position: 'relative',
                    maxWidth: 500,
                    margin: '0 auto',
                    [theme.breakpoints.down('md')]: {
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        width: '100%',
                        height: '100%',
                        maxWidth: '100%',
                        borderRadius: 0,
                        zIndex: 1300,
                        display: 'flex',
                        flexDirection: 'column',
                        px: 0,
                        py: 0,
                    }
                }
                : {
                    height: '100vh',
                    width: '100vw',
                    bgcolor: theme.palette.mode === 'dark'
                        ? alpha(theme.palette.background.default, 0.95)
                        : alpha(theme.palette.grey[100], 0.95),
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    zIndex: 1300,
                    overflow: 'auto',
                    WebkitOverflowScrolling: 'touch',
                    [theme.breakpoints.down('md')]: {
                        position: 'absolute',
                        minHeight: '100vh',
                        minHeight: '100dvh',
                        height: 'auto',
                    }
                }
        }>
            {(!isModal) && (
                <IconButton 
                    onClick={handleClose}
                    sx={{
                        position: 'absolute',
                        right: 16,
                        top: 16,
                        zIndex: 1,
                        color: 'text.primary',
                        bgcolor: 'background.paper',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        '&:hover': {
                            bgcolor: 'background.paper',
                        },
                        [theme.breakpoints.down('md')]: {
                            position: isModal ? 'absolute' : 'fixed',
                            right: 8,
                            top: 8,
                            width: 40,
                            height: 40,
                        }
                    }}
                >
                    <CloseIcon />
                </IconButton>
            )}
            <Box sx={{
                [theme.breakpoints.down('md')]: {
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }
            }}>
                {isModal ? (
                    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', bgcolor: theme.palette.background.default }}>
                        {/* Sticky Header */}
                        <Box
                            sx={{
                                display: { xs: 'flex', sm: 'flex', md: 'none' },
                                background: theme => theme.palette.primary.main,
                                color: 'white',
                                px: { xs: 0, sm: 0, md: 1.5 },
                                py: { xs: 1.5, sm: 2.5 },
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                borderBottom: '1px solid rgba(255,255,255,0.08)',
                                position: 'sticky',
                                top: 0,
                                zIndex: 20,
                                width: '100%',
                                minHeight: { xs: 60, sm: 'unset' },
                               
                            }}
                        >
                            <Box sx={{ ml: { xs: '19px', sm: '19px', md: 0 } }}>
                                <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, mb: 0.5, fontSize: '1.25rem' }}>Add Funds</Typography>
                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>Add money to your trading account</Typography>
                            </Box>
                            <IconButton
                                onClick={handleClose}
                                sx={{ color: 'white', ml: 2, background: 'rgba(255,255,255,0.08)', '&:hover': { background: 'rgba(255,255,255,0.18)' } }}
                            >
                                <CloseIcon />
                            </IconButton>
                        </Box>
                        {/* Scrollable Content - REMOVE Container for mobile modal */}
                        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch', pb: '80px' }}>
                            {/* Place all modal content (Paper, etc.) directly here, do not wrap in Container for mobile modal */}
                            <Paper
                                elevation={0}
                                sx={{
                                    borderRadius: 4,
                                    overflow: 'hidden',
                                    border: `1px solid ${theme.palette.divider}`,
                                    [theme.breakpoints.down('md')]: {
                                        borderRadius: 3,
                                    }
                                }}
                            >
                                <Box sx={{
                                    p: 3,
                                    bgcolor: theme.palette.mode === 'dark'
                                        ? alpha(theme.palette.success.main, 0.1)
                                        : alpha(theme.palette.success.light, 0.1),
                                    borderBottom: `1px solid ${theme.palette.divider}`,
                                    [theme.breakpoints.down('md')]: {
                                        p: 2,
                                    }
                                }}>
                                    <Typography variant="h6" sx={{ 
                                        color: theme.palette.success.main, 
                                        fontWeight: 600,
                                        [theme.breakpoints.down('md')]: {
                                            fontSize: '1.1rem',
                                        }
                                    }}>
                                        Enter Amount
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                        Enter the amount you want to add to your trading account
                                    </Typography>
                                </Box>

                                <Box sx={{ 
                                    p: 3,
                                    [theme.breakpoints.down('md')]: {
                                        p: 2,
                                    }
                                }}>
                                    <TextField
                                        fullWidth
                                        label="Amount"
                                        value={amount}
                                        onChange={handleAmountChange}
                                        error={Boolean(amountError)}
                                        helperText={amountError}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">₹</InputAdornment>
                                            ),
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <Tooltip title="Min: ₹100 | Max: ₹10,00,000">
                                                        <InfoOutlinedIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                                                    </Tooltip>
                                                </InputAdornment>
                                            )
                                        }}
                                        sx={{
                                            '& input': {
                                                fontSize: '1.5rem',
                                                fontWeight: 500,
                                                textAlign: 'center',
                                                py: 2,
                                                [theme.breakpoints.down('md')]: {
                                                    fontSize: '1.25rem',
                                                    py: 1.5,
                                                }
                                            }
                                        }}
                                    />
                                </Box>
                            </Paper>

                            <Paper
                                elevation={0}
                                sx={{
                                    borderRadius: 4,
                                    overflow: 'hidden',
                                    border: `1px solid ${theme.palette.divider}`,
                                    [theme.breakpoints.down('md')]: {
                                        borderRadius: 3,
                                    }
                                }}
                            >
                                <Box sx={{
                                    p: 3,
                                    bgcolor: theme.palette.mode === 'dark'
                                        ? alpha(theme.palette.primary.main, 0.1)
                                        : alpha(theme.palette.primary.light, 0.1),
                                    borderBottom: `1px solid ${theme.palette.divider}`,
                                    [theme.breakpoints.down('md')]: {
                                        p: 2,
                                    }
                                }}>
                                    <Typography variant="h6" sx={{ 
                                        color: theme.palette.primary.main, 
                                        fontWeight: 600,
                                        [theme.breakpoints.down('md')]: {
                                            fontSize: '1.1rem',
                                        }
                                    }}>
                                        Select Bank Account
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                        Choose the bank account for fund transfer
                                    </Typography>
                                </Box>

                                <Box sx={{ 
                                    p: 3,
                                    [theme.breakpoints.down('md')]: {
                                        p: 2,
                                    }
                                }}>
                                    {bankAccounts.length > 0 ? (
                                        <Stack spacing={3} sx={{
                                            [theme.breakpoints.down('md')]: {
                                                spacing: 2,
                                            }
                                        }}>
                                            <Box sx={{ 
                                                display: 'flex', 
                                                flexDirection: 'column',
                                                gap: 2
                                            }}>
                                                {(showAllBanks
                                                    ? bankAccounts
                                                    : bankAccounts.filter(bank => bank.bank_account_number === selectedBank)
                                                ).map((bank, index, arr) => (
                                                    <React.Fragment key={bank.bank_account_number}>
                                                        <Box
                                                            onClick={() => setSelectedBank(bank.bank_account_number)}
                                                            sx={{
                                                                p: 2.5,
                                                                borderRadius: 3,
                                                                cursor: 'pointer',
                                                                bgcolor: selectedBank === bank.bank_account_number
                                                                    ? theme.palette.mode === 'dark'
                                                                        ? alpha(theme.palette.primary.main, 0.15)
                                                                        : alpha(theme.palette.primary.light, 0.15)
                                                                    : theme.palette.mode === 'dark'
                                                                        ? alpha(theme.palette.primary.main, 0.05)
                                                                        : alpha(theme.palette.primary.light, 0.05),
                                                                border: `1px solid ${selectedBank === bank.bank_account_number
                                                                    ? theme.palette.primary.main
                                                                    : alpha(theme.palette.primary.main, 0.1)}`,
                                                                transition: 'all 0.2s ease',
                                                                '&:hover': {
                                                                    bgcolor: selectedBank === bank.bank_account_number
                                                                        ? theme.palette.mode === 'dark'
                                                                            ? alpha(theme.palette.primary.main, 0.2)
                                                                            : alpha(theme.palette.primary.light, 0.2)
                                                                        : theme.palette.mode === 'dark'
                                                                            ? alpha(theme.palette.primary.main, 0.1)
                                                                            : alpha(theme.palette.primary.light, 0.1)
                                                                },
                                                                [theme.breakpoints.down('md')]: {
                                                                    p: 2,
                                                                    borderRadius: 2,
                                                                }
                                                            }}
                                                        >
                                                            <Box sx={{ 
                                                                display: 'flex', 
                                                                alignItems: 'center', 
                                                                gap: 2, 
                                                                mb: 2,
                                                                [theme.breakpoints.down('md')]: {
                                                                    gap: 1.5,
                                                                    mb: 1.5,
                                                                }
                                                            }}>
                                                                <Box sx={{
                                                                    width: 48,
                                                                    height: 48,
                                                                    borderRadius: 2,
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    bgcolor: selectedBank === bank.bank_account_number
                                                                        ? alpha(theme.palette.primary.main, 0.2)
                                                                        : alpha(theme.palette.primary.main, 0.1),
                                                                    [theme.breakpoints.down('md')]: {
                                                                        width: 40,
                                                                        height: 40,
                                                                    }
                                                                }}>
                                                                    <AccountBalanceIcon
                                                                        sx={{
                                                                            color: theme.palette.primary.main,
                                                                            fontSize: 24,
                                                                            [theme.breakpoints.down('md')]: {
                                                                                fontSize: 20,
                                                                            }
                                                                        }}
                                                                    />
                                                                </Box>
                                                                <Box sx={{ flex: 1 }}>
                                                                    <Typography variant="h6" sx={{
                                                                        color: theme.palette.text.primary,
                                                                        fontWeight: 600,
                                                                        [theme.breakpoints.down('md')]: {
                                                                            fontSize: '1rem',
                                                                        }
                                                                    }}>
                                                                        {bank.bank_name}
                                                                    </Typography>
                                                                    <Typography variant="body2" sx={{
                                                                        color: theme.palette.primary.main,
                                                                        fontWeight: 500,
                                                                        [theme.breakpoints.down('md')]: {
                                                                            fontSize: '0.75rem',
                                                                        }
                                                                    }}>
                                                                        {index === 0 ? 'Primary Account' : 'Secondary Account'}
                                                                    </Typography>
                                                                </Box>
                                                                <Box sx={{
                                                                    width: 20,
                                                                    height: 20,
                                                                    borderRadius: '50%',
                                                                    border: `2px solid ${selectedBank === bank.bank_account_number
                                                                        ? theme.palette.primary.main
                                                                        : theme.palette.divider}`,
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    [theme.breakpoints.down('md')]: {
                                                                        width: 16,
                                                                        height: 16,
                                                                    }
                                                                }}>
                                                                    {selectedBank === bank.bank_account_number && (
                                                                        <Box sx={{
                                                                            width: 12,
                                                                            height: 12,
                                                                            borderRadius: '50%',
                                                                            bgcolor: theme.palette.primary.main,
                                                                            [theme.breakpoints.down('md')]: {
                                                                                width: 8,
                                                                                height: 8,
                                                                            }
                                                                        }} />
                                                                    )}
                                                                </Box>
                                                            </Box>
                                                            <Stack spacing={2} sx={{
                                                                [theme.breakpoints.down('md')]: {
                                                                    spacing: 1.5,
                                                                }
                                                            }}>
                                                                <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                                                                    <Box sx={{ flex: 1 }}>
                                                                        <Typography variant="caption" color="text.secondary">Account Number</Typography>
                                                                        <Typography variant="body2" fontWeight="500" sx={{ [theme.breakpoints.down('md')]: { fontSize: '0.875rem' } }}>{bank.bank_account_number}</Typography>
                                                                    </Box>
                                                                    <Box sx={{ flex: 1 }}>
                                                                        <Typography variant="caption" color="text.secondary">IFSC Code</Typography>
                                                                        <Typography variant="body2" fontWeight="500" sx={{ [theme.breakpoints.down('md')]: { fontSize: '0.875rem' } }}>{bank.ifsc}</Typography>
                                                                    </Box>
                                                                </Box>
                                                                <Box sx={{ display: 'flex', gap: 2 }}>
                                                                    <Box sx={{ flex: 1 }}>
                                                                        <Typography variant="caption" color="text.secondary">Branch</Typography>
                                                                        <Typography variant="body2" fontWeight="500" sx={{ [theme.breakpoints.down('md')]: { fontSize: '0.875rem' } }}>{bank.bank_branch_name || 'N/A'}</Typography>
                                                                    </Box>
                                                                    <Box sx={{ flex: 1 }}>
                                                                        <Typography variant="caption" color="text.secondary">Location</Typography>
                                                                        <Typography variant="body2" fontWeight="500" sx={{ [theme.breakpoints.down('md')]: { fontSize: '0.875rem' } }}>{bank.city}, {bank.state}</Typography>
                                                                    </Box>
                                                                </Box>
                                                            </Stack>
                                                        </Box>
                                                        {/* Show More after last visible card if not expanded and more than one bank */}
                                                        {!showAllBanks && bankAccounts.length > 1 && index === arr.length - 1 && (
                                                            <Box sx={{ width: '100%', textAlign: 'center', mt: 0.5, pt: 0 }}>
                                                                <Button
                                                                    variant="text"
                                                                    fullWidth
                                                                    sx={{
                                                                        fontWeight: 600,
                                                                        color: theme.palette.primary.main,
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        py: 1.5
                                                                    }}
                                                                    onClick={() => setShowAllBanks(true)}
                                                                    endIcon={<ExpandMoreIcon />}
                                                                >
                                                                    Show More
                                                                </Button>
                                                            </Box>
                                                        )}
                                                        {/* Show Less after last card if expanded and more than one bank */}
                                                        {showAllBanks && bankAccounts.length > 1 && index === arr.length - 1 && (
                                                            <Box sx={{ width: '100%', textAlign: 'center', mt: 1, mb: { xs: '70px', sm: '70px', md: 0 } }}>
                                                                <Button
                                                                    variant="text"
                                                                    fullWidth
                                                                    sx={{
                                                                        fontWeight: 600,
                                                                        color: theme.palette.primary.main,
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        py: 1.5
                                                                    }}
                                                                    onClick={() => setShowAllBanks(false)}
                                                                    endIcon={<ExpandLessIcon />}
                                                                >
                                                                    Show Less
                                                                </Button>
                                                            </Box>
                                                        )}
                                                    </React.Fragment>
                                                ))}
                                            </Box>
                                        </Stack>
                                    ) : (
                                        <Box sx={{
                                            p: 4,
                                            textAlign: 'center',
                                            borderRadius: 3,
                                            border: `2px dashed ${alpha(theme.palette.primary.main, 0.2)}`,
                                            bgcolor: theme.palette.mode === 'dark'
                                                ? alpha(theme.palette.primary.main, 0.05)
                                                : alpha(theme.palette.primary.light, 0.05),
                                            mb: 3,
                                            [theme.breakpoints.down('md')]: {
                                                p: 3,
                                                borderRadius: 2,
                                            }
                                        }}>
                                            <AccountBalanceIcon
                                                sx={{
                                                    fontSize: 48,
                                                    color: alpha(theme.palette.primary.main, 0.5),
                                                    mb: 2,
                                                    [theme.breakpoints.down('md')]: {
                                                        fontSize: 40,
                                                    }
                                                }}
                                            />
                                            <Typography variant="h6" sx={{ 
                                                mb: 1, 
                                                color: 'text.primary',
                                                [theme.breakpoints.down('md')]: {
                                                    fontSize: '1.1rem',
                                                }
                                            }}>
                                                No Bank Account Found
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                                Please add a bank account to proceed with adding funds
                                            </Typography>
                                            <Button
                                                variant="contained"
                                                startIcon={<AddIcon />}
                                                onClick={handleAddBankClick}
                                                sx={{
                                                    borderRadius: 2,
                                                    px: 3,
                                                    [theme.breakpoints.down('md')]: {
                                                        px: 2,
                                                        py: 1.5,
                                                    }
                                                }}
                                            >
                                                Add Bank Account
                                            </Button>
                                        </Box>
                                    )}
                                </Box>
                            </Paper>
                            {/* Sticky Add Funds Button */}
                            <Box
                                sx={{
                                    position: 'sticky',
                                    bottom: 0,
                                    left: 0,
                                    width: '100%',
                                    bgcolor: 'background.paper',
                                    p: 2,
                                    borderTop: theme => `1px solid ${theme.palette.divider}`,
                                    zIndex: 10,
                                    boxShadow: '0 -2px 8px rgba(0,0,0,0.04)',
                                    [theme.breakpoints.down('md')]: {
                                        position: 'fixed',
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        p: 2,
                                        pb: 'calc(2px + env(safe-area-inset-bottom))',
                                        bgcolor: theme.palette.background.paper,
                                        zIndex: 11
                                    }
                                }}
                            >
                                <Button
                                    variant="contained"
                                    size="large"
                                    fullWidth
                                    endIcon={<ArrowForwardIcon />}
                                    onClick={handleAddFunds}
                                    sx={{
                                        py: 1.5,
                                        borderRadius: 2,
                                        bgcolor: theme => theme.palette.success.main,
                                        '&:hover': { bgcolor: theme => theme.palette.success.dark },
                                        fontWeight: 600,
                                        fontSize: '1.1rem',
                                    }}
                                >
                                    Add Funds
                                </Button>
                            </Box>
                        </Box>
                    </Box>
                ) : (
                    <Box sx={{
                        flex: 1,
                        overflowY: 'auto',
                        minHeight: 0,
                        WebkitOverflowScrolling: 'touch',
                        pb: '80px',
                        [theme.breakpoints.down('md')]: {
                            flex: 1,
                            minHeight: 0,
                            overflowY: 'auto',
                            WebkitOverflowScrolling: 'touch',
                            pb: '80px',
                        }
                    }}>
                        <Container 
                            maxWidth="md" 
                            sx={isModal ? { 
                                py: 2, 
                                mt: 2, 
                                pb: 2, 
                                px: 0,
                                [theme.breakpoints.down('md')]: {
                                    py: 0,
                                    mt: 0,
                                    pb: 0,
                                    px: 0,
                                    height: '100%',
                                    marginLeft: 0,
                                    marginRight: 0,
                                    width: '100%',
                                    maxWidth: '100%',
                                }
                            } : {
                                py: 4,
                                mt: 4,
                                pb: 6,
                                [theme.breakpoints.down('md')]: {
                                    px: 2,
                                    py: 3,
                                    mt: 6,
                                    pb: 8,
                                }
                            }}
                        >
                            <Box sx={{
                                maxWidth: 600,
                                margin: '0 auto',
                                width: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 3,
                                [theme.breakpoints.down('md')]: {
                                    gap: 2,
                                    maxWidth: '100%',
                                    margin: 0,
                                    px: 2,
                                    mb: 2
                                }
                            }}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        borderRadius: 4,
                                        overflow: 'hidden',
                                        border: `1px solid ${theme.palette.divider}`,
                                        [theme.breakpoints.down('md')]: {
                                            borderRadius: 3,
                                        }
                                    }}
                                >
                                    <Box sx={{
                                        p: 3,
                                        bgcolor: theme.palette.mode === 'dark'
                                            ? alpha(theme.palette.success.main, 0.1)
                                            : alpha(theme.palette.success.light, 0.1),
                                        borderBottom: `1px solid ${theme.palette.divider}`,
                                        [theme.breakpoints.down('md')]: {
                                            p: 2,
                                        }
                                    }}>
                                        <Typography variant="h6" sx={{ 
                                            color: theme.palette.success.main, 
                                            fontWeight: 600,
                                            [theme.breakpoints.down('md')]: {
                                                fontSize: '1.1rem',
                                            }
                                        }}>
                                            Enter Amount
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                            Enter the amount you want to add to your trading account
                                        </Typography>
                                    </Box>

                                    <Box sx={{ 
                                        p: 3,
                                        [theme.breakpoints.down('md')]: {
                                            p: 2,
                                        }
                                    }}>
                                        <TextField
                                            fullWidth
                                            label="Amount"
                                            value={amount}
                                            onChange={handleAmountChange}
                                            error={Boolean(amountError)}
                                            helperText={amountError}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">₹</InputAdornment>
                                                ),
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <Tooltip title="Min: ₹100 | Max: ₹10,00,000">
                                                            <InfoOutlinedIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                                                        </Tooltip>
                                                    </InputAdornment>
                                                )
                                            }}
                                            sx={{
                                                '& input': {
                                                    fontSize: '1.5rem',
                                                    fontWeight: 500,
                                                    textAlign: 'center',
                                                    py: 2,
                                                    [theme.breakpoints.down('md')]: {
                                                        fontSize: '1.25rem',
                                                        py: 1.5,
                                                    }
                                                }
                                            }}
                                        />
                                    </Box>
                                </Paper>

                                <Paper
                                    elevation={0}
                                    sx={{
                                        borderRadius: 4,
                                        overflow: 'hidden',
                                        border: `1px solid ${theme.palette.divider}`,
                                        [theme.breakpoints.down('md')]: {
                                            borderRadius: 3,
                                        }
                                    }}
                                >
                                    <Box sx={{
                                        p: 3,
                                        bgcolor: theme.palette.mode === 'dark'
                                            ? alpha(theme.palette.primary.main, 0.1)
                                            : alpha(theme.palette.primary.light, 0.1),
                                        borderBottom: `1px solid ${theme.palette.divider}`,
                                        [theme.breakpoints.down('md')]: {
                                            p: 2,
                                        }
                                    }}>
                                        <Typography variant="h6" sx={{ 
                                            color: theme.palette.primary.main, 
                                            fontWeight: 600,
                                            [theme.breakpoints.down('md')]: {
                                                fontSize: '1.1rem',
                                            }
                                        }}>
                                            Select Bank Account
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                            Choose the bank account for fund transfer
                                        </Typography>
                                    </Box>

                                    <Box sx={{ 
                                        p: 3,
                                        [theme.breakpoints.down('md')]: {
                                            p: 2,
                                        }
                                    }}>
                                        {bankAccounts.length > 0 ? (
                                            <Stack spacing={3} sx={{
                                                [theme.breakpoints.down('md')]: {
                                                    spacing: 2,
                                                }
                                            }}>
                                                <Box sx={{ 
                                                    display: 'flex', 
                                                    flexDirection: 'column',
                                                    gap: 2
                                                }}>
                                                    {(showAllBanks
                                                        ? bankAccounts
                                                        : bankAccounts.filter(bank => bank.bank_account_number === selectedBank)
                                                    ).map((bank, index, arr) => (
                                                        <React.Fragment key={bank.bank_account_number}>
                                                            <Box
                                                                onClick={() => setSelectedBank(bank.bank_account_number)}
                                                                sx={{
                                                                    p: 2.5,
                                                                    borderRadius: 3,
                                                                    cursor: 'pointer',
                                                                    bgcolor: selectedBank === bank.bank_account_number
                                                                        ? theme.palette.mode === 'dark'
                                                                            ? alpha(theme.palette.primary.main, 0.15)
                                                                            : alpha(theme.palette.primary.light, 0.15)
                                                                        : theme.palette.mode === 'dark'
                                                                            ? alpha(theme.palette.primary.main, 0.05)
                                                                            : alpha(theme.palette.primary.light, 0.05),
                                                                    border: `1px solid ${selectedBank === bank.bank_account_number
                                                                        ? theme.palette.primary.main
                                                                        : alpha(theme.palette.primary.main, 0.1)}`,
                                                                    transition: 'all 0.2s ease',
                                                                    '&:hover': {
                                                                        bgcolor: selectedBank === bank.bank_account_number
                                                                            ? theme.palette.mode === 'dark'
                                                                                ? alpha(theme.palette.primary.main, 0.2)
                                                                                : alpha(theme.palette.primary.light, 0.2)
                                                                            : theme.palette.mode === 'dark'
                                                                                ? alpha(theme.palette.primary.main, 0.1)
                                                                                : alpha(theme.palette.primary.light, 0.1)
                                                                    },
                                                                    [theme.breakpoints.down('md')]: {
                                                                        p: 2,
                                                                        borderRadius: 2,
                                                                    }
                                                                }}
                                                            >
                                                                <Box sx={{ 
                                                                    display: 'flex', 
                                                                    alignItems: 'center', 
                                                                    gap: 2, 
                                                                    mb: 2,
                                                                    [theme.breakpoints.down('md')]: {
                                                                        gap: 1.5,
                                                                        mb: 1.5,
                                                                    }
                                                                }}>
                                                                    <Box sx={{
                                                                        width: 48,
                                                                        height: 48,
                                                                        borderRadius: 2,
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        bgcolor: selectedBank === bank.bank_account_number
                                                                            ? alpha(theme.palette.primary.main, 0.2)
                                                                            : alpha(theme.palette.primary.main, 0.1),
                                                                        [theme.breakpoints.down('md')]: {
                                                                            width: 40,
                                                                            height: 40,
                                                                        }
                                                                    }}>
                                                                        <AccountBalanceIcon
                                                                            sx={{
                                                                                color: theme.palette.primary.main,
                                                                                fontSize: 24,
                                                                                [theme.breakpoints.down('md')]: {
                                                                                    fontSize: 20,
                                                                                }
                                                                            }}
                                                                        />
                                                                    </Box>
                                                                    <Box sx={{ flex: 1 }}>
                                                                        <Typography variant="h6" sx={{
                                                                            color: theme.palette.text.primary,
                                                                            fontWeight: 600,
                                                                            [theme.breakpoints.down('md')]: {
                                                                                fontSize: '1rem',
                                                                            }
                                                                        }}>
                                                                            {bank.bank_name}
                                                                        </Typography>
                                                                        <Typography variant="body2" sx={{
                                                                            color: theme.palette.primary.main,
                                                                            fontWeight: 500,
                                                                            [theme.breakpoints.down('md')]: {
                                                                                fontSize: '0.75rem',
                                                                            }
                                                                        }}>
                                                                            {index === 0 ? 'Primary Account' : 'Secondary Account'}
                                                                        </Typography>
                                                                    </Box>
                                                                    <Box sx={{
                                                                        width: 20,
                                                                        height: 20,
                                                                        borderRadius: '50%',
                                                                        border: `2px solid ${selectedBank === bank.bank_account_number
                                                                            ? theme.palette.primary.main
                                                                            : theme.palette.divider}`,
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        [theme.breakpoints.down('md')]: {
                                                                            width: 16,
                                                                            height: 16,
                                                                        }
                                                                    }}>
                                                                        {selectedBank === bank.bank_account_number && (
                                                                            <Box sx={{
                                                                                width: 12,
                                                                                height: 12,
                                                                                borderRadius: '50%',
                                                                                bgcolor: theme.palette.primary.main,
                                                                                [theme.breakpoints.down('md')]: {
                                                                                    width: 8,
                                                                                    height: 8,
                                                                                }
                                                                            }} />
                                                                        )}
                                                                    </Box>
                                                                </Box>
                                                                <Stack spacing={2} sx={{
                                                                    [theme.breakpoints.down('md')]: {
                                                                        spacing: 1.5,
                                                                    }
                                                                }}>
                                                                    <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                                                                        <Box sx={{ flex: 1 }}>
                                                                            <Typography variant="caption" color="text.secondary">Account Number</Typography>
                                                                            <Typography variant="body2" fontWeight="500" sx={{ [theme.breakpoints.down('md')]: { fontSize: '0.875rem' } }}>{bank.bank_account_number}</Typography>
                                                                        </Box>
                                                                        <Box sx={{ flex: 1 }}>
                                                                            <Typography variant="caption" color="text.secondary">IFSC Code</Typography>
                                                                            <Typography variant="body2" fontWeight="500" sx={{ [theme.breakpoints.down('md')]: { fontSize: '0.875rem' } }}>{bank.ifsc}</Typography>
                                                                        </Box>
                                                                    </Box>
                                                                    <Box sx={{ display: 'flex', gap: 2 }}>
                                                                        <Box sx={{ flex: 1 }}>
                                                                            <Typography variant="caption" color="text.secondary">Branch</Typography>
                                                                            <Typography variant="body2" fontWeight="500" sx={{ [theme.breakpoints.down('md')]: { fontSize: '0.875rem' } }}>{bank.bank_branch_name || 'N/A'}</Typography>
                                                                        </Box>
                                                                        <Box sx={{ flex: 1 }}>
                                                                            <Typography variant="caption" color="text.secondary">Location</Typography>
                                                                            <Typography variant="body2" fontWeight="500" sx={{ [theme.breakpoints.down('md')]: { fontSize: '0.875rem' } }}>{bank.city}, {bank.state}</Typography>
                                                                        </Box>
                                                                    </Box>
                                                                </Stack>
                                                            </Box>
                                                            {/* Show More after last visible card if not expanded and more than one bank */}
                                                            {!showAllBanks && bankAccounts.length > 1 && index === arr.length - 1 && (
                                                                <Box sx={{ width: '100%', textAlign: 'center', mt: 0.5, pt: 0 }}>
                                                                    <Button
                                                                        variant="text"
                                                                        fullWidth
                                                                        sx={{
                                                                            fontWeight: 600,
                                                                            color: theme.palette.primary.main,
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center',
                                                                            py: 1.5
                                                                        }}
                                                                        onClick={() => setShowAllBanks(true)}
                                                                        endIcon={<ExpandMoreIcon />}
                                                                    >
                                                                        Show More
                                                                    </Button>
                                                                </Box>
                                                            )}
                                                            {/* Show Less after last card if expanded and more than one bank */}
                                                            {showAllBanks && bankAccounts.length > 1 && index === arr.length - 1 && (
                                                                <Box sx={{ width: '100%', textAlign: 'center', mt: 1, mb: { xs: '70px', sm: '70px', md: 0 } }}>
                                                                    <Button
                                                                        variant="text"
                                                                        fullWidth
                                                                        sx={{
                                                                            fontWeight: 600,
                                                                            color: theme.palette.primary.main,
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center',
                                                                            py: 1.5
                                                                        }}
                                                                        onClick={() => setShowAllBanks(false)}
                                                                        endIcon={<ExpandLessIcon />}
                                                                    >
                                                                        Show Less
                                                                    </Button>
                                                                </Box>
                                                            )}
                                                        </React.Fragment>
                                                    ))}
                                                </Box>
                                            </Stack>
                                        ) : (
                                            <Box sx={{
                                                p: 4,
                                                textAlign: 'center',
                                                borderRadius: 3,
                                                border: `2px dashed ${alpha(theme.palette.primary.main, 0.2)}`,
                                                bgcolor: theme.palette.mode === 'dark'
                                                    ? alpha(theme.palette.primary.main, 0.05)
                                                    : alpha(theme.palette.primary.light, 0.05),
                                                mb: 3,
                                                [theme.breakpoints.down('md')]: {
                                                    p: 3,
                                                    borderRadius: 2,
                                                }
                                            }}>
                                                <AccountBalanceIcon
                                                    sx={{
                                                        fontSize: 48,
                                                        color: alpha(theme.palette.primary.main, 0.5),
                                                        mb: 2,
                                                        [theme.breakpoints.down('md')]: {
                                                            fontSize: 40,
                                                        }
                                                    }}
                                                />
                                                <Typography variant="h6" sx={{ 
                                                    mb: 1, 
                                                    color: 'text.primary',
                                                    [theme.breakpoints.down('md')]: {
                                                        fontSize: '1.1rem',
                                                    }
                                                }}>
                                                    No Bank Account Found
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                                    Please add a bank account to proceed with adding funds
                                                </Typography>
                                                <Button
                                                    variant="contained"
                                                    startIcon={<AddIcon />}
                                                    onClick={handleAddBankClick}
                                                    sx={{
                                                        borderRadius: 2,
                                                        px: 3,
                                                        [theme.breakpoints.down('md')]: {
                                                            px: 2,
                                                            py: 1.5,
                                                        }
                                                    }}
                                                >
                                                    Add Bank Account
                                                </Button>
                                            </Box>
                                        )}
                                    </Box>
                                </Paper>
                            </Box>
                        </Container>
                    </Box>
                )}
            </Box>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={5000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                sx={{
                    mt: 7,
                    mr: 2,
                    [theme.breakpoints.down('md')]: {
                        mt: 8,
                        mr: 1,
                        ml: 1,
                        '& .MuiSnackbar-root': {
                            left: 8,
                            right: 8,
                        }
                    }
                }}
            >
                <Alert 
                    onClose={handleCloseSnackbar} 
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ 
                        minWidth: '300px',
                        boxShadow: theme => `0 4px 12px ${alpha(theme.palette.error.main, 0.15)}`,
                        '& .MuiAlert-message': {
                            fontSize: '0.875rem',
                            fontWeight: 500
                        },
                        '& .MuiAlert-icon': {
                            fontSize: '1.25rem'
                        },
                        [theme.breakpoints.down('md')]: {
                            minWidth: 'auto',
                            width: '100%',
                        }
                    }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>

            <AddBankModal
                open={showAddBankModal}
                onClose={handleAddBankClose}
            />
        </Box>
    );
};

export default PayIn; 